import React, { useEffect, useState, useCallback } from 'react';
import {
  Business as BuildingIcon,
  Star as StarIcon,
  HighlightOff as CancelIcon,
  Warning as WarningIcon,
  AttachMoney as DollarIcon,
  HelpOutline as HelpIcon,
  Visibility as ViewIcon,
  Lock as LockIcon,
  Schedule as ClockIcon,
} from '@mui/icons-material';
import { Box, Typography, Tooltip } from '@mui/material';
import {
  StyledContainer,
  StyledContent,
  ProfileCard,
  ProfileRow,
  ProfileMeta,
  StatusBadgeRow,
  StatusPill,
  StatusInnerBadge,
  StatusExplanationCard,
  StatCardsRow,
  MetricsGrid,
  MetricsColumn,
  MetricCard,
  MetricCardHeader,
  MetricCardTitle,
  PercentileBadge,
  MetricStatRow,
  MetricStatLabel,
  MetricStatValue,
  IgnoredNote,
  FeedbackRow,
  FeedbackCategoryDot,
  ResumeButton,
  LockedMetricsContainer,
  LockedIconCircle,
  ProgressNarrowWrapper,
  RewardCard,
  RewardIconBox,
  RewardProgressPanel,
  RewardProgressTitle,
  RewardProgressTrack,
  RewardProgressFill,
  RewardCTAWhite,
  UnderlineLink,
  GrandfatherSection,
  GrandfatherTitleRow,
  GrandfatherClockIcon,
  GrandfatherProgressTrack,
  GrandfatherProgressFill,
  GrandfatherMessage,
  MembershipStatusBanner,
  MembershipBannerTitle,
  MembershipBannerBody,
  MembershipRestrictionList,
  MembershipRestrictionLine,
  MembershipCourseList,
  MembershipCourseListItem,
  MembershipCourseCtaButton,
} from './MyStats.styles';
import { Analytics } from 'utils/analytics';
import { useHistory } from 'react-router-dom';
import { useActionApi, usePersistedState } from 'hooks';
import Api from 'api';
import TalentNavbar from 'components/TalentNavbar';
import TalentPageLayout from 'components/TalentPageLayout';
import TalentBackButton from 'components/TalentBackButton/TalentBackButton';
import FixedLoader from 'components/FixedLoader';
import { entities } from 'reduxStore';
import { HygienistProps, MembershipStatusResponse } from 'utils/types';
import TalentResume from 'components/TalentResume';
import ReferralCodeCard from 'components/ReferralCodeCard';
import MembershipBannerMessage from 'components/MembershipBannerMessage';
import Popup from 'components/Popup';
import { getMembershipBannerCopy, getMembershipRestrictionLines, openMembershipPath, shouldRenderMembershipNextAction, shouldShowMembershipBannerOnMyStats } from 'utils/membership';
import { getPerformanceScoreDisplay } from 'utils/performanceScoreDisplay';
import StatCardComponent from './components/StatCardComponent';
import RetentionGuidance from './components/RetentionGuidance';
import UrgentShiftRewardsCard from './components/UrgentShiftRewardsCard';
import dayjs from 'dayjs';
// AvatarWithInitials removed from header per design — name only, no avatar
import {
  getPercentileLabelColor,
  getRoleLabel,
  getStatusBadgeColors,
  ffsCodeToDisplayLabel,
} from './utils';
import type { MyStatsResponse } from './utils';

const FEEDBACK_CATEGORIES = [
  'Clinical Skills',
  'Professionalism',
  'Communication & Patient Care',
  'Time Management & Punctuality',
];

const NOT_A_FIT_SUPPORT_EMAIL = 'help@tempstars.com';
const GRANDFATHERING_WINDOW_DAYS = 90;
const LEGACY_GRANDFATHERING_WINDOW_DAYS = [365, 455, 545];

const resolveGrandfatheringWindowDays = (daysRemaining: number, explicitWindowDays?: number | null): number => {
  if (explicitWindowDays && explicitWindowDays > 0) return explicitWindowDays;
  if (daysRemaining <= GRANDFATHERING_WINDOW_DAYS) return GRANDFATHERING_WINDOW_DAYS;
  return LEGACY_GRANDFATHERING_WINDOW_DAYS.find(windowDays => daysRemaining <= windowDays)
    || daysRemaining;
};

export const MyStats: React.FC = () => {
  const history = useHistory();
  const [myStats, setMyStats] = useState<MyStatsResponse | null>(null);
  const [membershipStatus, setMembershipStatus] = useState<MembershipStatusResponse | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [resumeData, setResumeData] = useState<any>(null);

  const hygienist = usePersistedState<HygienistProps>({ entityName: entities.hygienist })[0];
  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
  const navFirstName = hygienist?.firstName || currentUser?.hygienist?.firstName || '';
  const navLastName = hygienist?.lastName || currentUser?.hygienist?.lastName || '';
  const navPhotoUrl = hygienist?.photoUrl || currentUser?.hygienist?.photoUrl || undefined;
  const isHygOrAsst = (hygienist?.type ?? currentUser?.hygienist?.type) === (window as any).TempStars?.HYGIENIST_TYPES?.HYGIENIST ||
    (hygienist?.type ?? currentUser?.hygienist?.type) === (window as any).TempStars?.HYGIENIST_TYPES?.ASSISTANT;

  const [statsLoading, , getStats] = useActionApi({
    apiMethod: Api.getMyStats,
    onSuccess: (data: MyStatsResponse) => setMyStats(data),
  });

  useEffect(() => { getStats({}); }, []);

  useEffect(() => {
    if (!myStats) return;
    const state = (history.location.state || {}) as { scrollToRewards?: boolean; milestone?: number };
    if (!state.scrollToRewards && state.milestone == null) return;

    let cancelled = false;
    const startedAt = Date.now();

    const scrollCardIntoView = (): boolean => {
      const el = document.getElementById('urgent-shift-rewards-card');
      if (!el) return false;

      // Nested TalentPageLayout / StyledContainer uses overflow:auto — scroll that scroller.
      let scroller: HTMLElement | null = el.parentElement;
      while (scroller) {
        const style = window.getComputedStyle(scroller);
        const canScroll =
          /(auto|scroll)/.test(style.overflowY)
          && scroller.scrollHeight > scroller.clientHeight + 1;
        if (canScroll) break;
        scroller = scroller.parentElement;
      }

      // Use instant scroll — smooth scrolls get cancelled by layout retries.
      if (scroller) {
        const scrollerRect = scroller.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const nextTop = scroller.scrollTop + (elRect.top - scrollerRect.top) - 12;
        scroller.scrollTo({ top: Math.max(0, nextTop), behavior: 'auto' });
      } else {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
      }

      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight * 0.85 && rect.bottom > 0;
    };

    const tick = () => {
      if (cancelled) return;
      if (scrollCardIntoView() || Date.now() - startedAt > 3500) return;
      window.setTimeout(tick, 150);
    };

    const first = window.setTimeout(tick, 50);
    return () => {
      cancelled = true;
      window.clearTimeout(first);
    };
  }, [myStats, history.location.state]);

  useEffect(() => {
    let isMounted = true;

    const loadMembership = async () => {
      setMembershipLoading(true);
      try {
        const membership = await Api.getMembershipStatus();
        if (isMounted) {
          setMembershipStatus(membership);
        }
      } catch {
        if (isMounted) {
          setMembershipStatus(null);
        }
      } finally {
        if (isMounted) {
          setMembershipLoading(false);
        }
      }
    };

    loadMembership();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!myStats) return;
    Analytics.track('Viewed My Stats', {
      membershipStatusCode: membershipStatus?.currentStatusCode ?? null,
      membershipDisplayLabel: membershipStatus?.displayLabel ?? null,
      tempShiftAccess: membershipStatus?.tempShiftAccess ?? null,
      membershipOverlayState: membershipStatus?.overlay?.state ?? null,
      membershipOverlayActive: membershipStatus?.overlay?.active ?? false,
      membershipOverlaySource: membershipStatus?.overlay?.source ?? null,
      platform: (window as any).cordova ? 'mobile' : 'web',
    });
  }, [myStats, membershipStatus]);

  const onGoHome = () => {
    history.push('loading');
    (window as any).TempStars?.App?.goReact('hygienistHome');
  };

  const ffsStatusCode: string | null = membershipStatus?.currentStatusCode || myStats?.membershipStatusCode || null;
  const visibleStatusLabel = membershipStatus?.displayLabel || myStats?.membershipDisplayLabel || (myStats?.isProvisional ? 'Provisional' : ffsCodeToDisplayLabel(ffsStatusCode));
  const currentBadgeColors = getStatusBadgeColors(visibleStatusLabel);
  const membershipBanner = membershipStatus
    ? getMembershipBannerCopy(membershipStatus, { firstName: navFirstName })
    : null;
  const hideBookedShiftLimitInMyStats = membershipStatus?.currentStatusCode === 'RL2FL1';
  const membershipRestrictionLines = getMembershipRestrictionLines(membershipStatus?.restrictions, {
    hideBookedShiftLimit: hideBookedShiftLimitInMyStats,
  });
  const shouldHideRedundantRestrictionAction = membershipStatus?.tempShiftAccess === 'restricted';
  const shouldRenderNextAction = shouldRenderMembershipNextAction(membershipStatus, membershipBanner, {
    hideReviewRestrictions: shouldHideRedundantRestrictionAction,
  });
  const reliabilityScoreDisplay = getPerformanceScoreDisplay(myStats?.reliabilityLabel, myStats?.reliabilityPercentile);
  const feedbackScoreDisplay = getPerformanceScoreDisplay(myStats?.feedbackLabel, myStats?.feedbackPercentile);
  const reliabilityDisplayLabel = reliabilityScoreDisplay?.text || (myStats && myStats.reliabilityPercentile == null ? 'Building reliability history' : null);
  const feedbackDisplayLabel = feedbackScoreDisplay?.text || (myStats && myStats.feedbackPercentile == null ? 'Building feedback history' : null);
  const retentionInsights = membershipStatus?.retentionInsights;
  const showRetentionGuidance = retentionInsights?.visible === true;
  const shouldShowStatusExplanation = !!myStats && !myStats.isProvisional && (
    !!membershipStatus || !!reliabilityDisplayLabel || !!feedbackDisplayLabel
  );
  const grandfatheringUntil = membershipStatus?.grandfathering.active && membershipStatus.grandfathering.until
    ? dayjs(membershipStatus.grandfathering.until)
    : null;
  const showGrandfatherCountdown = !!grandfatheringUntil && grandfatheringUntil.isValid();
  // Prefer the backend-owned time-remaining fields from retention insights;
  // fall back to the legacy client-side window inference otherwise.
  const grandfatherDaysRemaining = showGrandfatherCountdown
    ? (showRetentionGuidance && retentionInsights?.daysRemaining != null
      ? Math.max(0, retentionInsights.daysRemaining)
      : Math.max(0, Math.ceil(grandfatheringUntil.diff(dayjs(), 'day', true))))
    : 0;
  const grandfatherWindowDays = showGrandfatherCountdown
    ? (showRetentionGuidance && retentionInsights?.protectionWindowDays != null && retentionInsights.protectionWindowDays > 0
      ? retentionInsights.protectionWindowDays
      : resolveGrandfatheringWindowDays(grandfatherDaysRemaining, membershipStatus?.grandfathering.windowDays))
    : GRANDFATHERING_WINDOW_DAYS;
  const grandfatherDaysElapsed = showGrandfatherCountdown
    ? Math.max(0, grandfatherWindowDays - grandfatherDaysRemaining)
    : 0;
  const grandfatherProgressPct = showGrandfatherCountdown
    ? Math.min(100, (grandfatherDaysElapsed / grandfatherWindowDays) * 100)
    : 0;
  const membershipRecommendedCourses = membershipStatus?.recommendedCourses?.length
    ? membershipStatus.recommendedCourses
    : membershipStatus?.recommendedCourse
      ? [membershipStatus.recommendedCourse]
      : [];
  const protectedStatusLabel = membershipStatus?.grandfathering.protectedStatusCode
    ? ffsCodeToDisplayLabel(membershipStatus.grandfathering.protectedStatusCode)
    : visibleStatusLabel;
  const calculatedStatusLabel = membershipStatus?.grandfathering.calculatedStatusCode
    ? ffsCodeToDisplayLabel(membershipStatus.grandfathering.calculatedStatusCode)
    : null;
  const shouldShowAccountReviewSupportLink = membershipStatus?.overlay?.reasonCategory !== 'credential_review'
    && (
      membershipStatus?.overlay?.state === 'on_a_break'
      || membershipStatus?.overlay?.state === 'not_a_fit'
      || membershipStatus?.statusCategory === 'ffs_recovery_on_a_break'
      || membershipStatus?.statusCategory === 'provisional_on_a_break'
      || membershipStatus?.statusCategory === 'ffs_recovery_not_a_fit'
      || membershipStatus?.statusCategory === 'provisional_not_a_fit'
    );

  const isMobile = !!(window as any).cordova;

  const openNotFitSupportEmail = useCallback(() => {
    const subject = 'TempStars account review question';
    const body = 'Hello TempStars Support,\n\nI have a question about my membership status and account review.\n\nThank you.';

    if ((window as any).cordova?.plugins?.email?.open) {
      (window as any).cordova.plugins.email.open({ to: NOT_A_FIT_SUPPORT_EMAIL, subject, body });
      return;
    }

    window.open(
      `mailto:${NOT_A_FIT_SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      '_system',
    );
  }, []);

  const isCourseRequiredRecovery = membershipRecommendedCourses.length > 0
    && (
      membershipStatus?.overlay?.state === 'course_required'
      || membershipStatus?.statusCategory === 'ffs_recovery_course_required'
      || membershipStatus?.statusCategory === 'provisional_course_required'
    );
  const membershipCourseHeading = isCourseRequiredRecovery
    ? `Required course${membershipRecommendedCourses.length > 1 ? 's' : ''}`
    : `Based on your profile trends, we recommend the following course${membershipRecommendedCourses.length > 1 ? 's' : ''}:`;
  const membershipCourseCtaLabel = isCourseRequiredRecovery
    ? membershipRecommendedCourses.length > 1
      ? 'Take Required Courses Now'
      : 'Take the Course Now'
    : membershipRecommendedCourses.length > 1
      ? 'View Recommended Courses'
      : 'View Recommended Course';
  const openMembershipCourse = useCallback(() => {
    if (isCourseRequiredRecovery && membershipRecommendedCourses.length === 1) {
      const lmsCourseId = membershipRecommendedCourses[0].lmsCourseId;
      if (lmsCourseId != null) {
        history.push({ pathname: 'coursePlayer', state: { lmsCourseId } });
        return;
      }
    }

    openMembershipPath(history, 'coursesHome');
  }, [history, isCourseRequiredRecovery, membershipRecommendedCourses]);

  const tooltipOrAlert = (text: string, children: React.ReactElement) => {
    if (isMobile) {
      return React.cloneElement(children, {
        onClick: (e: React.MouseEvent) => { e.stopPropagation(); alert(text); },
      });
    }
    return <Tooltip title={text} arrow placement="top">{children}</Tooltip>;
  };

  const openResume = async () => {
    Analytics.track('my_stats_resume_click', {});
    try {
      const rd = await Api.getResumeData({ hygienistId: hygienist?.id, locationId: 0 });
      setResumeData(rd);
    } catch {
      setResumeData({
        ...hygienist,
        completedJobs: myStats?.completedJobs ?? hygienist?.completedJobs,
        shiftHero: !!(
          myStats?.urgentShiftRewards?.shiftHero
          ?? myStats?.urgentShiftRewards?.shiftSaviour
        ),
        shiftSaviour: !!(
          myStats?.urgentShiftRewards?.shiftHero
          ?? myStats?.urgentShiftRewards?.shiftSaviour
        ),
      });
    }
    setShowResume(true);
  };

  const referralAndResumeCards = (
    <>
      <ReferralCodeCard audience="talent" country={hygienist?.country === 1 ? 1 : 0} />
      <MetricCard data-testid="my-stats-professional-profile">
        <Typography variant="h6" fontWeight="bold" mb={1}>Professional Profile</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Keep your clinical details updated to improve your matching rate and feedback scores.
        </Typography>
        <ResumeButton
          id="my-stats-view-resume-button"
          data-testid="my-stats-view-resume-button"
          aria-label="View Resume"
          variant="outlined"
          onClick={openResume}
          className="no-fastclick"
        >
          <ViewIcon sx={{ fontSize: '1rem' }} />
          View Resume
        </ResumeButton>
      </MetricCard>
    </>
  );

  return (
    <>
      <TalentPageLayout
        navbar={(
          <TalentNavbar
            firstName={navFirstName}
            lastName={navLastName}
            photoUrl={navPhotoUrl}
            isHygOrAsst={isHygOrAsst}
            instabookActivated={(hygienist?.InstabookActivated ?? currentUser?.hygienist?.InstabookActivated) === 1}
            isTestAccount={(hygienist as any)?.isTest === 1 || (currentUser?.hygienist as any)?.isTest === 1}
            showAddShiftButton={false}
            showJobBoardButton={false}
          />
        )}
      >
      <StyledContainer>
        <TalentBackButton onBack={onGoHome} />
        <StyledContent>
          {/* ---- Profile Header Card ---- */}
          <ProfileCard>
            <ProfileRow>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {hygienist?.firstName} {hygienist?.lastName}
                </Typography>
                <ProfileMeta>
                  {getRoleLabel(hygienist?.type)}
                  {myStats?.joinedDate ? ` · Joined ${dayjs(myStats.joinedDate).format('MMMM YYYY')}` : ''}
                </ProfileMeta>
              </Box>

              <StatusBadgeRow>
                {showGrandfatherCountdown ? (
                  <StatusPill $bg="#FEF7E0" $color="#8B6914" $borderColor="#F0DFA0">
                    <Typography variant="caption" fontWeight={600}>Protected Rollout Status</Typography>
                    <StatusInnerBadge $bg={currentBadgeColors.bg} $color={currentBadgeColors.color}>
                      {visibleStatusLabel}
                    </StatusInnerBadge>
                  </StatusPill>
                ) : (
                  <StatusPill $bg={currentBadgeColors.bg} $color={currentBadgeColors.color}>
                    {visibleStatusLabel}{visibleStatusLabel !== 'Provisional' && visibleStatusLabel !== 'Account Suspended' ? ' Status' : ''}
                  </StatusPill>
                )}
              </StatusBadgeRow>
            </ProfileRow>
          </ProfileCard>

          {showGrandfatherCountdown && grandfatheringUntil && (
            <GrandfatherSection>
              <GrandfatherTitleRow>
                <GrandfatherClockIcon><ClockIcon /></GrandfatherClockIcon>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>Fair Fresh Start rollout protection</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Protected until {grandfatheringUntil.format('MMMM D, YYYY')}
                  </Typography>
                </Box>
              </GrandfatherTitleRow>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Typography variant="body2" color="text.secondary">
                  {grandfatherDaysRemaining} day{grandfatherDaysRemaining === 1 ? '' : 's'} remaining
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(grandfatherProgressPct)}% elapsed
                </Typography>
              </Box>
              <GrandfatherProgressTrack>
                <GrandfatherProgressFill $widthPct={grandfatherProgressPct} />
              </GrandfatherProgressTrack>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">Day 1</Typography>
                <Typography variant="caption" color="text.secondary">Day {grandfatherWindowDays}</Typography>
              </Box>
              {/* When retention insights are visible they own the actionable guidance copy,
                  so the countdown keeps only a short status summary plus the protection caveat. */}
              <GrandfatherMessage>
                <Typography variant="body2" sx={{ color: '#0B4598' }}>
                  {showRetentionGuidance ? (
                    <>
                      Your protected status is {protectedStatusLabel}.
                      {calculatedStatusLabel ? ` Projected after protection: ${calculatedStatusLabel}.` : ''}
                    </>
                  ) : (
                    <>
                      Your protected status is {protectedStatusLabel}.
                      {calculatedStatusLabel
                        ? ` Fair Fresh Start currently calculates your upcoming status as ${calculatedStatusLabel}.`
                        : ''}
                      {' '}Complete eligible shifts and keep strong reliability and feedback to maintain or improve your status before protection ends.
                    </>
                  )}
                  {' '}Keep in mind: protection does not grant immunity — continued poor feedback or shift cancellations can still cause your status to drop.
                </Typography>
              </GrandfatherMessage>
            </GrandfatherSection>
          )}

          {showRetentionGuidance && retentionInsights && membershipStatus && (
            <RetentionGuidance
              insights={retentionInsights}
              membershipStatus={membershipStatus}
              onOpenPath={(path) => openMembershipPath(history, path)}
            />
          )}

          {membershipStatus && shouldShowMembershipBannerOnMyStats(membershipStatus) && membershipBanner && (
            <MembershipStatusBanner $tone={membershipBanner.tone}>
              <MembershipBannerTitle variant="subtitle1">
                {membershipBanner.title}
              </MembershipBannerTitle>
              <MembershipBannerBody variant="body2" sx={{ mb: membershipRestrictionLines.length ? 0 : 1 }}>
                <MembershipBannerMessage copy={membershipBanner} />
              </MembershipBannerBody>

              {/* The grandfathering group is an internal audit field and must
                  never be shown to Talent (T2-6060). */}
              {membershipStatus.grandfathering.active && membershipStatus.grandfathering.until && (
                <Typography variant="body2" mb={1}>
                  Protected until <strong>{dayjs(membershipStatus.grandfathering.until).format('MMMM D, YYYY')}</strong>.
                </Typography>
              )}

              {membershipRestrictionLines.length > 0 && (
                <MembershipRestrictionList>
                  {membershipRestrictionLines.map((line) => (
                    <MembershipRestrictionLine key={line} variant="body2">
                      {line}
                    </MembershipRestrictionLine>
                  ))}
                </MembershipRestrictionList>
              )}

              {shouldRenderNextAction && (
                <Typography variant="body2" sx={{ mt: membershipRestrictionLines.length ? 1 : 0.5 }}>
                  <strong>{membershipStatus.nextAction.title}:</strong> {membershipStatus.nextAction.description}
                </Typography>
              )}

              {membershipRecommendedCourses.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {membershipCourseHeading}
                  </Typography>
                  <MembershipCourseList>
                    {membershipRecommendedCourses.map(course => (
                      <MembershipCourseListItem key={course.code}>
                        {course.title}
                      </MembershipCourseListItem>
                    ))}
                  </MembershipCourseList>
                  <MembershipCourseCtaButton
                    variant="contained"
                    color="primary"
                    onClick={openMembershipCourse}
                  >
                    {membershipCourseCtaLabel}
                  </MembershipCourseCtaButton>
                </Box>
              )}

              {shouldShowAccountReviewSupportLink && (
                <Typography component="div" variant="body2" sx={{ mt: 1 }}>
                  Need help?{' '}
                  <UnderlineLink
                    variant="body2"
                    color="primary"
                    onClick={openNotFitSupportEmail}
                    sx={{ display: 'inline' }}
                  >
                    Email TempStars support
                  </UnderlineLink>
                  .
                </Typography>
              )}
            </MembershipStatusBanner>
          )}

          {shouldShowStatusExplanation && (
            <StatusExplanationCard>
              <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                About your Fair Fresh Start status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your status is based on weighted reliability, weighted feedback, completed shifts,
                and rated shifts. Percentile labels compare your results with other active Talent,
                so they may not move exactly like your visible status.
              </Typography>
            </StatusExplanationCard>
          )}

          {/* ---- Five Stat Cards ---- */}
          <StatCardsRow>
            <StatCardComponent
              icon={<BuildingIcon />}
              iconBg="#EBF0F9"
              iconColor="#0B4598"
              title="Completed Jobs"
              value={`${myStats?.completedJobs ?? 0}`}
            />
            <StatCardComponent
              icon={<StarIcon />}
              iconBg="#FFF8E1"
              iconColor="#F59E0B"
              title="Rated Jobs"
              value={`${myStats?.ratedJobs ?? 0}`}
            />
            <StatCardComponent
              icon={<CancelIcon />}
              iconBg="#FEF3F3"
              iconColor="#FB7672"
              title="Total Cancellations"
              value={`${myStats?.totalCancellationsLifetime ?? 0}`}
            />
            <StatCardComponent
              icon={<WarningIcon />}
              iconBg="#FFF3E0"
              iconColor="#F57C00"
              title="Cancellations affecting your account"
              value={`${myStats?.cancellationsAffectingAccount ?? 0}`}
              subtext={(myStats?.ignoredCancellations ?? 0) > 0 ? `(${myStats?.ignoredCancellations} have been ignored)` : undefined}
            />
            <StatCardComponent
              icon={<DollarIcon />}
              iconBg="#E8F9F1"
              iconColor="#13CA82"
              title="Total Earned"
              value={`$${(myStats?.totalEarned ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            />
          </StatCardsRow>

          {/* T2-6328: Urgent Shift rewards card (separate from Reliability SNC metric below) */}
          <UrgentShiftRewardsCard rewards={myStats?.urgentShiftRewards} />

          {/* ---- Provisional Lock ---- */}
          {myStats?.isProvisional ? (
            <>
              <LockedMetricsContainer>
                <LockedIconCircle><LockIcon /></LockedIconCircle>
                <Typography variant="h6" fontWeight="bold" mb={0.5}>Metrics Locked</Typography>
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  Complete {myStats?.metricsUnlock?.goalShifts || 5} shifts to unlock your detailed performance metrics!
                </Typography>
                <Box mb={1.5}>
                  <Typography variant="body2" fontWeight={600}>Reliability</Typography>
                  <Typography variant="body2" color="text.secondary">Building reliability history</Typography>
                  <Typography variant="body2" fontWeight={600} mt={1}>Overall Feedback</Typography>
                  <Typography variant="body2" color="text.secondary">Building feedback history</Typography>
                </Box>
                <ProgressNarrowWrapper>
                  <Box bgcolor="#e5e7eb" borderRadius={9999} height={10}>
                    <Box
                      bgcolor="#13CA82"
                      height={10}
                      borderRadius={9999}
                      width={`${Math.min(100, ((myStats?.metricsUnlock?.completedShifts || 0) / (myStats?.metricsUnlock?.goalShifts || 5)) * 100)}%`}
                    />
                  </Box>
                </ProgressNarrowWrapper>
                <Typography variant="body2" color="text.secondary">
                  {`${myStats?.metricsUnlock?.completedShifts || 0} of ${myStats?.metricsUnlock?.goalShifts || 5} shifts completed`}
                </Typography>
              </LockedMetricsContainer>

              {myStats?.newMemberReward?.show && (
                <RewardCard $bg="#0B4598">
                  <RewardIconBox>🎁</RewardIconBox>
                  <Typography variant="h6" fontWeight="bold" mb={1}>New Member Reward</Typography>
                  <Typography variant="body2" mb={2}>Complete 3 shifts within 30 days of signing up and receive a $25 Amazon Gift Card via email!</Typography>
                  <RewardProgressPanel>
                    <RewardProgressTitle variant="body2">Your Progress</RewardProgressTitle>
                    <RewardProgressTrack>
                      <RewardProgressFill $widthPct={(myStats?.newMemberReward?.completedShifts || 0) / 3 * 100} />
                    </RewardProgressTrack>
                    <Typography variant="body2">{`${myStats?.newMemberReward?.completedShifts || 0} of 3 shifts completed`}</Typography>
                  </RewardProgressPanel>
                  <Box mt={2}>
                    <RewardCTAWhite className="no-fastclick" onClick={() => history.push({ pathname: 'hygienistHome' })}>Find Your Next Shift</RewardCTAWhite>
                  </Box>
                </RewardCard>
              )}
              {referralAndResumeCards}
            </>
          ) : (
            /* ---- Performance Metrics (2-column) ---- */
            <MetricsGrid>
              <MetricsColumn>
                {/* Reliability Score */}
                <MetricCard>
                  <MetricCardHeader>
                    <MetricCardTitle>
                      Reliability Score
                      {tooltipOrAlert(
                        'Fair Fresh Start status uses weighted reliability plus shift-count requirements. The percentile label compares your reliability with other active Talent and may not directly match your visible status.',
                        <HelpIcon fontSize="small" sx={{ cursor: 'pointer', color: '#9e9e9e' }} />
                      )}
                    </MetricCardTitle>
                    {reliabilityDisplayLabel && (
                      <PercentileBadge $bg={getPercentileLabelColor(reliabilityScoreDisplay?.label || null)}>
                        {reliabilityDisplayLabel}
                      </PercentileBadge>
                    )}
                  </MetricCardHeader>
                  <MetricStatRow>
                    <MetricStatLabel>Short Notice Cancellations</MetricStatLabel>
                    <MetricStatValue>{myStats?.shortNoticeCancellations ?? 0}</MetricStatValue>
                  </MetricStatRow>
                  <MetricStatRow>
                    <MetricStatLabel>Grace Periods Available</MetricStatLabel>
                    <MetricStatValue>{`${myStats?.gracePeriodsAvailable ?? 0}/3`}</MetricStatValue>
                  </MetricStatRow>
                  <MetricStatRow>
                    <MetricStatLabel>Total Cancellations</MetricStatLabel>
                    <MetricStatValue>{myStats?.cancellationsAffectingAccount ?? 0}</MetricStatValue>
                  </MetricStatRow>
                  {(myStats?.ignoredCancellations ?? 0) > 0 && (
                    <IgnoredNote>
                      <Typography variant="body2" sx={{ color: '#13CA82' }}>
                        {myStats?.ignoredCancellations} cancellation{(myStats?.ignoredCancellations ?? 0) !== 1 ? 's' : ''} were ignored.
                      </Typography>
                    </IgnoredNote>
                  )}
                </MetricCard>

                {/* Feedback Score */}
                <MetricCard>
                  <MetricCardHeader>
                    <MetricCardTitle>
                      Feedback Score
                      {tooltipOrAlert(
                        'Fair Fresh Start status uses weighted feedback plus rated-shift requirements. The percentile label compares your feedback with other active Talent and may not directly match your visible status.',
                        <HelpIcon fontSize="small" sx={{ cursor: 'pointer', color: '#9e9e9e' }} />
                      )}
                    </MetricCardTitle>
                    {feedbackDisplayLabel && (
                      <PercentileBadge $bg={getPercentileLabelColor(feedbackScoreDisplay?.label || null)}>
                        {feedbackDisplayLabel}
                      </PercentileBadge>
                    )}
                  </MetricCardHeader>
                  {FEEDBACK_CATEGORIES.map((cat) => (
                    <FeedbackRow key={cat}>
                      <Box display="flex" alignItems="center">
                        <FeedbackCategoryDot />
                        <Typography variant="body2">{cat}</Typography>
                      </Box>
                    </FeedbackRow>
                  ))}
                </MetricCard>
              </MetricsColumn>

              <MetricsColumn>
                {referralAndResumeCards}
              </MetricsColumn>
            </MetricsGrid>
          )}
        </StyledContent>
      </StyledContainer>
      </TalentPageLayout>

      {showResume && (
        <Popup opened={showResume} onClose={() => setShowResume(false)}>
          <TalentResume hygienist={resumeData || hygienist} onClose={() => setShowResume(false)} />
        </Popup>
      )}
      <FixedLoader loading={statsLoading || membershipLoading} />
    </>
  );
};
